import React from 'react';
import {DocumentReadWReferences, DocumentType, getShorthand} from '../../schemas';
import {SlArrowLeft, SlArrowRight} from 'react-icons/sl';
import {useEnhancedDocumentsContext} from '../../contexts/doc_contexts';
import {IoClose} from 'react-icons/io5';
import {MdOpenInFull, MdCloseFullscreen} from 'react-icons/md';

interface DocumentToolsPanelProps {
    doc: DocumentReadWReferences;
    onClose: () => void;
    showNav?: boolean;
    onExpand?: () => void;
    onCollapse?: () => void;
    children?: React.ReactNode;
}

const DocumentToolsPanel: React.FC<DocumentToolsPanelProps> = ({
    doc,
    onClose,
    showNav = false,
    onExpand,
    onCollapse,
    children,
}) => {
    const {
        canGoPrev,
        canGoNext,
        goToPrevDocument,
        goToNextDocument,
        navLoading,
    } = useEnhancedDocumentsContext()!;

    return (
        <div className="flex flex-col h-full w-full bg-slate-50/80 overflow-hidden">
            {/* ── Panel header: close + title + nav ── */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200 bg-white/80 backdrop-blur-sm shrink-0">
                <button
                    onClick={onClose}
                    title="Close"
                    className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                    <IoClose className="w-4 h-4" />
                </button>

                {doc.type && (
                    <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${
                        doc.type === DocumentType.TRANSCRIPT
                            ? 'bg-indigo-50 text-indigo-600 ring-indigo-200'
                            : doc.type === DocumentType.EMAIL
                                ? 'bg-sky-50 text-sky-600 ring-sky-200'
                                : 'bg-slate-50 text-slate-600 ring-slate-200'
                    }`}>
                        {doc.type}
                    </span>
                )}
                <h3 className="flex-1 text-sm font-semibold text-slate-700 truncate" title={getShorthand(doc)}>
                    {getShorthand(doc)}
                </h3>

                {/* Expand / Collapse toggle */}
                {onExpand && (
                    <button
                        onClick={onExpand}
                        title="Expand to full view"
                        className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                        <MdOpenInFull className="w-4 h-4" />
                    </button>
                )}
                {onCollapse && (
                    <button
                        onClick={onCollapse}
                        title="Collapse to side panel"
                        className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                        <MdCloseFullscreen className="w-4 h-4" />
                    </button>
                )}

                {showNav && (
                    <div className="flex items-center gap-1.5">
                        {canGoPrev && (
                            <button
                                onClick={() => goToPrevDocument()}
                                disabled={navLoading}
                                className={`p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors ${navLoading ? 'opacity-50 cursor-wait' : ''}`}
                                title="Previous document"
                            >
                                <SlArrowLeft className="w-4 h-4" />
                            </button>
                        )}
                        {canGoNext && (
                            <button
                                onClick={() => goToNextDocument()}
                                disabled={navLoading}
                                className={`p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors ${navLoading ? 'opacity-50 cursor-wait' : ''}`}
                                title="Next document"
                            >
                                <SlArrowRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ── Child content (document view) ── */}
            {children && (
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    {children}
                </div>
            )}
        </div>
    );
};

export default DocumentToolsPanel;
